// La celda de crédito de un cliente en la cartera y el crédito vigente de un acreditado. Puro.
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { LoanPortfolioSciSource } from '@/api/services/loan-credit';

/** Créditos vigentes: los que aún no se cierran, la mora incluida. Son los que suman al saldo. */
const OPEN_CREDIT_STATUSES: LoanCreditStatusEnum[] = [
    LoanCreditStatusEnum.PENDING_ACTIVATION,
    LoanCreditStatusEnum.ACTIVE,
    LoanCreditStatusEnum.IN_ARREARS,
];

/** De dónde salió el SCI que se muestra. `null` es que no hay SCI del que decirlo. */
export type CreditSciSource = Exclude<LoanPortfolioSciSource, 'NONE'>;

export interface CreditPortfolioEntry {
    /** El acreditado del cliente, la llave de sus lecturas de crédito y de scoring. `null` es que
     *  todavía no tiene expediente, no que no se haya podido leer. */
    borrowerId: string | null;
    /** Todos los créditos del acreditado, vigentes o no — la columna «CR». */
    creditsCount: number;
    /** Créditos VIVOS, la mora incluida. La regla un-crédito-a-la-vez lo deja en 1; >1 es anomalía
     *  de datos y el saldo deja de ser el de `activeCreditId`. */
    activeCreditsCount: number;
    /** Capital que aún se debe sobre los créditos vigentes. */
    outstandingBalanceCents: number;
    /** Días desde la cuota vencida más vieja. `0` = al día; `null` = no hay cuotas que mirar. */
    arrearsDays: number | null;
    /** SCI del cliente. `sciSource` dice si es el vivo o el congelado al originar su crédito. */
    sci: number | null;
    sciSource: CreditSciSource | null;
    clientLevel: ClientLevelEnum | null;
    hasActiveCredit: boolean;
    isInArrears: boolean;
    hasNoActiveCredit: boolean;
    isNotEligible: boolean;
    /** Créditos con el saldo pagado. Es el «Créditos liq.» del historial vitalicio. */
    settledCreditsCount: number;
    /** Créditos dados de baja contable por 180 días sin pago. Es el «Bajas cont.» del historial. */
    writtenOffCreditsCount: number;
    /** ¿Alguno de sus créditos quedó liquidado? Es el chip «Solo liquidados» del panel. */
    hasSettledCredit: boolean;
    /** El crédito vigente que la ficha 360 desglosa; `null` si no hay ninguno. */
    activeCreditId: string | null;
    /** Para el vendedor: tiene un crédito vivo en otra cadena, fuera de las cifras de arriba. */
    hasActiveCreditElsewhere: boolean;
}

export type CreditPortfolioCell =
    | { state: 'ready'; entry: CreditPortfolioEntry }
    | { state: 'no-source' }
    | { state: 'error' }
    | { state: 'forbidden' };

/** Estado que comparten TODAS las celdas: la cartera de crédito se lee de una sola pasada. */
export type CreditPortfolioState = 'loading' | 'ready' | 'no-source' | 'error' | 'forbidden';

export type CreditPortfolioMap = ReadonlyMap<string, CreditPortfolioCell>;

export const EMPTY_PORTFOLIO: CreditPortfolioMap = new Map<string, CreditPortfolioCell>();

/** El crédito más reciente del acreditado — el que congeló el SCI y el nivel vigentes. */
function latestCreditOf(credits: LoanCreditResponse[]): LoanCreditResponse | null {
    return [...credits].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).at(-1) ?? null;
}

/** ¿Este crédito todavía debe dinero? */
function isOpenCredit(credit: LoanCreditResponse): boolean {
    return OPEN_CREDIT_STATUSES.includes(credit.status);
}

/** El crédito vigente más reciente — el que la ficha 360 desglosa. */
export function openCreditOf(credits: LoanCreditResponse[]): LoanCreditResponse | null {
    return latestCreditOf(credits.filter(isOpenCredit));
}

/**
 * El estado que resume a toda la cartera. Gana el PEOR: los conteos de la barra hablan de la cartera
 * entera, así que con una sola celda sin leer no hay conteo honesto que dar.
 */
export function portfolioStateOf(cells: Iterable<CreditPortfolioCell>): CreditPortfolioState {
    const states = new Set([...cells].map((cell) => cell.state));
    if (states.has('forbidden')) return 'forbidden';
    if (states.has('error')) return 'error';
    if (states.has('no-source')) return 'no-source';
    return 'ready';
}

/**
 * Firma estable de una lista de clientes, para la `queryKey` de la cartera. La lista puede traer
 * 2000 ids y serializarla entera en cada render es más caro que resumirla.
 */
export function portfolioSignatureOf(retailCustomerIds: string[]): string {
    let hash = 0;
    for (const id of retailCustomerIds) {
        for (let i = 0; i < id.length; i += 1) {
            hash = (Math.imul(hash, 31) + id.charCodeAt(i)) | 0;
        }
    }
    return `${String(retailCustomerIds.length)}:${(hash >>> 0).toString(36)}`;
}
